/**
 * useMessageSync — mantém o on-device store de mensagens (IndexedDB) preenchido
 * para a busca local do chat.
 *
 * Montado 1× no shell (AppLayout), guardado por auth. Comportamento:
 *  - No mount (autenticado): roda um full/delta sync contra
 *    GET /api/v1/me/messages/sync (via runSync), paginando por cursor até
 *    esgotar, e persiste o SyncMeta (cursor + maior changed_at visto) ao final.
 *  - Reusa a conexão SSE única (useNotificationsRealtime.onEvent — NÃO abre
 *    conexão nova) para reagir a eventos de chat em tempo real:
 *      message.created / message.updated → agenda um delta-sync DEBOUNCED
 *      message.deleted                    → remove por id (imediato)
 *
 * NOTA (event shape): o payload SSE de chat NÃO carrega body/created_at — só
 * `{ type, conversation_id, message: { id, kind?, author_user_id? } }`. Por isso
 * NÃO gravamos mais um StoredMessage parcial (body-less) direto do evento — um
 * parcial nunca ficaria pesquisável (a busca só considera mensagens com body).
 * Em vez disso, um created/updated agenda um delta-sync debounced (1500ms) que
 * busca a(s) mensagem(ns) COM body decifrado do endpoint e as persiste → a
 * mensagem recém-recebida fica pesquisável ao vivo, sem esperar o próximo boot.
 * O delete continua sendo tratado direto (remove por id — não precisa de body).
 *
 * Concorrência: runSync é guardado por módulo (syncInFlight). Se um delta-sync
 * debounced disparar enquanto um sync está em voo, marca-se um rerun e roda-se
 * mais UMA vez ao terminar — nunca dois syncs em paralelo, nunca perde o delta.
 *
 * Best-effort em toda a linha: uma falha de sync nunca lança para o React.
 */
import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/store/auth'
import { fetchMessageSync } from '@/api/messageSync'
import { messageStore } from '@/lib/messageStore'
import {
  useNotificationsRealtime,
  type RealtimeEvent,
} from './useNotificationsRealtime'

/** Payload SSE de chat: campos extras sobre o RealtimeEvent base. */
type ChatRealtimeEvent = RealtimeEvent & {
  conversation_id?: number
  message?: {
    id?: number
    kind?: string
    author_user_id?: number | null
    body?: string | null
    created_at?: string
  }
}

/** Janela de debounce p/ coalescer uma rajada de eventos SSE em UM delta-sync. */
const SYNC_DEBOUNCE_MS = 1500

/** Guard de módulo: evita sync concorrente (StrictMode / multi-mount / debounce). */
let syncInFlight = false
/** Se um sync foi solicitado enquanto outro estava em voo, roda mais 1× ao fim. */
let syncRerunRequested = false

/** Maior string ISO entre a e b (null-safe). */
function maxIso(a: string | null, b: string | null): string | null {
  if (!a) return b
  if (!b) return a
  return a >= b ? a : b
}

/**
 * Full/delta sync incremental: lê SyncMeta → pagina fetchMessageSync(since,
 * changedSince) → persiste cada página → grava SyncMeta ao final.
 *
 * Concurrency-guarded: chamadas sobrepostas coalescem/serializam (nunca rodam em
 * paralelo). Se solicitado durante um sync em voo, re-executa uma vez ao terminar.
 * Best-effort: falhas são engolidas (backfill de busca não pode derrubar o app).
 */
async function runSync(): Promise<void> {
  if (syncInFlight) {
    // Já há um sync rodando — pede um rerun p/ capturar deltas que chegaram no meio.
    syncRerunRequested = true
    return
  }
  syncInFlight = true
  try {
    const meta = await messageStore.getSyncMeta()
    let since = meta?.last_cursor ?? 0
    const changedSince = meta?.last_changed_at ?? null
    let maxChanged = changedSince

    // Paginação por cursor: consome novas mensagens (id > since) + mutadas.
    //
    // Design de dois streams disjuntos: `changedSince` é FIXO durante toda a
    // paginação, então o stream de CHANGES (mutadas) é re-avaliado a cada
    // página do stream de NEW. Isso é intencional — o `put` é idempotente e
    // o store deduplica por id, então re-escrever as mesmas mutadas por página
    // é apenas desperdício inofensivo, não um bug.
    for (;;) {
      const res = await fetchMessageSync(since, changedSince)
      await messageStore.put(res.items)
      maxChanged = maxIso(maxChanged, res.changed_max)
      // Só continua quando o cursor AVANÇA estritamente. O backend sempre
      // devolve next_cursor > since; se uma resposta bugada/duplicada ecoar
      // next_cursor <= since, avançar mandaria o loop a re-buscar a mesma
      // página para sempre (prendendo syncInFlight = true e travando TODOS os
      // delta-syncs futuros). Aqui um bug do servidor vira parada graciosa.
      if (res.next_cursor != null && res.next_cursor > since) {
        since = res.next_cursor
        continue
      }
      break
    }

    await messageStore.setSyncMeta({ last_cursor: since, last_changed_at: maxChanged })
  } catch (err) {
    // Best-effort: sync é backfill de busca, não pode derrubar o app.
    console.warn('[useMessageSync] sync falhou (best-effort):', err)
  } finally {
    syncInFlight = false
    if (syncRerunRequested) {
      syncRerunRequested = false
      void runSync()
    }
  }
}

export function useMessageSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const didBootSync = useRef(false)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Boot/delta sync uma vez por sessão autenticada.
  useEffect(() => {
    if (!isAuthenticated || didBootSync.current) return
    didBootSync.current = true
    void runSync()
  }, [isAuthenticated])

  // Limpa o timer de debounce no unmount e cura os guards de módulo.
  useEffect(() => {
    return () => {
      if (debounceTimer.current != null) {
        clearTimeout(debounceTimer.current)
        debounceTimer.current = null
      }
      // Guards de módulo: reseta no unmount para não ficar preso entre um
      // logout/remount no mesmo ciclo de JS. Mantemos a premissa de mount
      // único, mas tornamos o guard auto-recuperável (self-healing).
      syncInFlight = false
      syncRerunRequested = false
    }
  }, [])

  // Agenda um delta-sync debounced: coalesce uma rajada de eventos em UM sync.
  const scheduleSync = () => {
    if (debounceTimer.current != null) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null
      void runSync()
    }, SYNC_DEBOUNCE_MS)
  }

  // Fold SSE — reusa a conexão única; só age quando autenticado.
  useNotificationsRealtime(
    {},
    {
      onEvent: (raw: ChatRealtimeEvent) => {
        if (!useAuthStore.getState().isAuthenticated) return
        try {
          if (raw.type === 'message.deleted') {
            // Delete não precisa de body: remove por id imediatamente.
            const id = raw.message?.id
            if (id != null) void messageStore.remove(id)
            return
          }
          if (raw.type === 'message.created' || raw.type === 'message.updated') {
            // Payload SSE não traz body; agenda delta-sync p/ buscar COM body.
            const id = raw.message?.id
            if (id != null && raw.conversation_id != null) scheduleSync()
          }
          // Demais tipos (reaction.updated, read, notificações) → ignorados.
        } catch (err) {
          // Nunca derrubar a SSE por um evento inesperado.
          console.warn('[useMessageSync] fold SSE falhou (best-effort):', err)
        }
      },
    },
  )
}
